import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  Payment,
  GetPaymentInput,
  ListPaymentsInput,
  ListPaymentsResponse,
  PreviewPaymentInput,
  PreviewPaymentResponse,
  RemoveInvoiceFromPaymentInput,
  RescheduleInvoiceInput,
  RescheduleInvoiceResponse,
  SimulatePaymentProviderStatusInput,
  SimulatePaymentProviderStatusResponse,
  VoidPaymentInput,
  VoidPaymentResponse,
  ExecutePaymentInput,
  ExecutePaymentResponse,
  RecordExternalPaymentInput,
  RecordExternalPaymentResponse,
  AiReviewSummary,
  RunAiReviewInput,
  RunAiReviewResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/** Invalidate all cached payment queries for a given payment ID. */
function invalidatePaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  paymentId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.finance.payments.get.queryKey({
      input: { id: paymentId },
    }),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.finance.payments.get.queryKey({
      input: { id: paymentId, includeProviderDetails: true },
    }),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.finance.payments.list.queryKey({
      input: {},
    }),
  });
}

/** Hook to fetch a list of payments with optional filtering. */
export function usePaymentsList(filters: ListPaymentsInput = {}) {
  const query = useQuery(
    orpc.finance.payments.list.queryOptions({
      input: filters,
    }),
  );

  const payments = query.data?.payments ?? [];
  const total = query.data?.total ?? 0;
  const totalAmount = query.data?.totalAmount ?? '0';

  return {
    ...query,
    payments,
    total,
    totalAmount,
  } satisfies typeof query & {
    data?: ListPaymentsResponse;
    payments: Payment[];
    total: number;
    totalAmount: string;
  };
}

interface UsePaymentOptions {
  /** Whether to enable the query (defaults to true if id is provided). */
  enabled?: boolean;
  /** Whether to include provider details like external payment status and images (defaults to false). */
  includeProviderDetails?: boolean;
}

/**
 * Hook to fetch a single payment by ID.
 * Optionally includes provider details like external payment status and images.
 */
export function usePayment(
  id: string | undefined,
  options?: UsePaymentOptions,
) {
  const { enabled = true, includeProviderDetails = false } = options ?? {};

  const query = useQuery({
    ...orpc.finance.payments.get.queryOptions({
      input: id
        ? ({
            id,
            ...(includeProviderDetails ? { includeProviderDetails: true } : {}),
          } satisfies GetPaymentInput)
        : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    payment: query.data ?? null,
  } satisfies typeof query & {
    data?: Payment | null;
    payment: Payment | null;
  };
}

/** Hook to preview a payment as a check image before execution. */
export function usePreviewPayment() {
  const { showToast } = useToast();

  return useMutation<PreviewPaymentResponse, Error, PreviewPaymentInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.preview(input);
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to preview payment',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/** Hook to void an executed payment and reverse its allocations. */
export function useVoidPayment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<VoidPaymentResponse, Error, VoidPaymentInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.void(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Payment voided successfully',
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.id);

      // Also invalidate invoice queries since voiding changes invoice paidAmount/paymentStatus/paidAt
      if (data.invoices && Array.isArray(data.invoices)) {
        for (const invoice of data.invoices) {
          void queryClient.invalidateQueries({
            queryKey: orpc.accountsPayable.invoices.get.queryKey({
              input: { id: invoice.id },
            }),
          });
        }
      }
      // Invalidate all invoice list queries as a fallback
      void queryClient.invalidateQueries({
        queryKey: ['accountsPayable.invoices.get'],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to void payment',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to (re-)execute an approved or failed payment that was never successfully
 * submitted to the provider.
 */
export function useExecutePayment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<ExecutePaymentResponse, Error, ExecutePaymentInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.execute(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Payment submitted successfully',
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.id);

      // Execution applies allocations to invoices (paidAmount/paymentStatus/paidAt),
      // so invalidate the affected invoice queries.
      if (data.invoices && Array.isArray(data.invoices)) {
        for (const invoice of data.invoices) {
          void queryClient.invalidateQueries({
            queryKey: orpc.accountsPayable.invoices.get.queryKey({
              input: { id: invoice.id },
            }),
          });
        }
      }
      void queryClient.invalidateQueries({
        queryKey: ['accountsPayable.invoices.get'],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to execute payment',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to record a payment as paid out-of-band (e.g. a manual bank ACH
 * transfer made outside the provider). No provider call is made; the payment
 * is marked paid and invoice allocations are applied server-side.
 */
export function useRecordExternalPayment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    RecordExternalPaymentResponse,
    Error,
    RecordExternalPaymentInput
  >({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.recordExternalPayment(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Payment recorded as paid',
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.id);

      // Recording applies allocations to invoices (paidAmount/paymentStatus/paidAt),
      // so invalidate the affected invoice queries.
      if (data.invoices && Array.isArray(data.invoices)) {
        for (const invoice of data.invoices) {
          void queryClient.invalidateQueries({
            queryKey: orpc.accountsPayable.invoices.get.queryKey({
              input: { id: invoice.id },
            }),
          });
        }
      }
      void queryClient.invalidateQueries({
        queryKey: ['accountsPayable.invoices.get'],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to record payment as paid',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/** Hook to remove an invoice from a draft payment. */
export function useRemoveInvoiceFromPayment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<Payment, Error, RemoveInvoiceFromPaymentInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.removeInvoice(input);
    },
    onSuccess: (_data, variables) => {
      showToast({
        title: 'Success',
        message: 'Invoice removed from payment',
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.paymentId);
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to remove invoice from payment',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/** Hook to reschedule an invoice from one payment to another date. */
export function useRescheduleInvoice() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<RescheduleInvoiceResponse, Error, RescheduleInvoiceInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.rescheduleInvoice(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Invoice rescheduled to new payment date',
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.paymentId);
      invalidatePaymentQueries(queryClient, data.targetPayment.id);
      queryClient.invalidateQueries({
        queryKey: orpc.finance.payments.list.key(),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to reschedule invoice',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/** Hook to simulate a payment provider status transition in sandbox mode. */
export function useSimulatePaymentProviderStatus() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    SimulatePaymentProviderStatusResponse,
    Error,
    SimulatePaymentProviderStatusInput
  >({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.simulateProviderStatus(input);
    },
    onSuccess: (_data, variables) => {
      showToast({
        title: 'Success',
        message: `Provider status updated to ${variables.status}`,
        color: 'success',
        icon: 'check',
      });

      invalidatePaymentQueries(queryClient, variables.id);
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update provider status',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to fetch the latest AI review summary for a payment. Polls every 3s
 * while a review is running.
 */
export function usePaymentAiReview(paymentId: string | undefined) {
  const query = useQuery({
    ...orpc.finance.payments.getAiReview.queryOptions({
      input: paymentId ? { paymentId } : skipToken,
    }),
    enabled: Boolean(paymentId),
    refetchInterval: (q) => {
      const data = q.state.data as AiReviewSummary | undefined;
      return data?.isRunning ? 3000 : false;
    },
  });

  return {
    ...query,
    review: query.data ?? null,
  } satisfies typeof query & {
    data?: AiReviewSummary;
    review: AiReviewSummary | null;
  };
}

/** Hook to kick off an AI payment review run. */
export function useRunPaymentAiReview() {
  const queryClient = useQueryClient();

  return useMutation<RunAiReviewResponse, Error, RunAiReviewInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.runAiReview(input);
    },
    onSuccess: (data, variables) => {
      const queryKey = orpc.finance.payments.getAiReview.queryKey({
        input: { paymentId: variables.paymentId },
      });

      // Optimistically pin the card into "running" state with the new run id.
      // This kicks the polling `refetchInterval` into motion immediately so
      // the UI doesn't rehydrate the prior completed summary and stop polling
      // before the reserved AgentRun is visible to the next read.
      if (data.agentRunId) {
        queryClient.setQueryData<AiReviewSummary>(queryKey, (prev) => ({
          status: 'running',
          isRunning: true,
          reviewPassed: null,
          reasoningSummary: null,
          issues: [],
          blockingReasons: [],
          checksPerformed: [],
          reviewedAt: null,
          agentRunId: data.agentRunId,
          error:
            prev?.agentRunId === data.agentRunId ? (prev?.error ?? null) : null,
        }));
      }

      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
